<script lang="ts">
	import type { MaterialProperty, MaterialPropertyListElement } from './types/materialProperty'
	import { materialPropertyListElementSchema } from './types/materialProperty'
	import { getContext, hasContext } from 'svelte'
	import { mmpvState } from './mmpv.state.svelte'
	import MaterialPropertySelection from './MaterialPropertySelection.svelte'
	import Button from '#lib/atoms/Button.svelte'
	import InputField from '#lib/molecules/InputField.svelte'
	import type { FontProps } from '$types/fontProps'
	import { DefaultInputFieldProps, DefaultRobotoFontProps } from '$types/fontProps'
	import { inputFieldFormats } from '$utils/formatters'
	import { t } from '$i18n/i18n'
	import Tooltip from '#lib/atoms/Tooltip.svelte'
	import { writable, type Writable } from 'svelte/store'
	import { getFormErrors } from '$utils/formValidation'
	import { TooltipPlacements } from '$types/enums'

	interface Props {
		propertyElement: MaterialPropertyListElement
		selectedProperty?: MaterialProperty | null
		onAddCreatePropertyBreadcrumb?: () => void
		onEditMaterialProperty?: (materialProperty: MaterialProperty) => void
	}

	let {
		propertyElement = $bindable(),
		selectedProperty = $bindable(),
		onAddCreatePropertyBreadcrumb,
		onEditMaterialProperty
	}: Props = $props()

	const fontProps: FontProps = DefaultInputFieldProps()
	const headerFontProps: FontProps = DefaultRobotoFontProps('15px', '0.875rem')
	const MATERIAL_PROPERTY_LIST_ELEMENT_FORM_ERROR_PATH_PREFIX = 'materialPropertyListElement.'

	const formErrors: Writable<
		{
			path: string
			message: string
		}[]
	> = hasContext('formErrors') ? getContext('formErrors') : writable([])

	let mappedProperties: MaterialProperty[] = $derived(
		propertyElement
			? propertyElement.propertyChildIds
					.map((propertyId: string) => mmpvState.materialProperties.get(propertyId))
					.filter((property: MaterialProperty | undefined) => property !== undefined)
			: []
	)

	function addPropertyToElement() {
		if (!selectedProperty) {
			return
		}
		// prevent error in list of properties to disable list of property with id new from store
		const propertyAlreadyAdded = propertyElement.propertyChildIds.includes(selectedProperty.id)
		if (selectedProperty.id !== 'new' && !propertyAlreadyAdded) {
			propertyElement = {
				...propertyElement,
				propertyChildIds: [...propertyElement.propertyChildIds, selectedProperty.id]
			}
		}
		selectedProperty = null // reset value to empty the input field
	}

	export async function saveMaterialPropertyListElement(element: MaterialPropertyListElement) {
		try {
			$formErrors = []
			let validatedPropertyListElement: MaterialPropertyListElement
			validatedPropertyListElement = await materialPropertyListElementSchema.validate(element, {
				stripUnknown: false,
				abortEarly: false
			})
			return validatedPropertyListElement
		} catch (error) {
			const err = getFormErrors(error, MATERIAL_PROPERTY_LIST_ELEMENT_FORM_ERROR_PATH_PREFIX)
			console.warn('ValidationError: ', err)
			$formErrors = err
			return null
		}
	}

	function removePropertyFromListElement(propertyId: string) {
		const indexOfProp = propertyElement.propertyChildIds.indexOf(propertyId)
		if (indexOfProp > -1) {
			const childIds = [...propertyElement.propertyChildIds]
			childIds.splice(indexOfProp, 1)
			propertyElement = { ...propertyElement, propertyChildIds: childIds }
			const currentParents = mmpvState.materialPropertyParents.get(propertyId)
			if (currentParents && propertyElement.propertyId !== null) {
				mmpvState.materialPropertyParents.set(
					propertyId,
					currentParents?.filter((parentId) => parentId !== propertyElement.propertyId)
				)
			}
		}
	}

	/**
	 * Constructs tooltip content for a property element that contains list elements.
	 * Content is built according to the number of list elements in the property.
	 * The options are: one, two or more list elements.
	 * We ignore 0, because we do not show the tooltip anyways
	 * @param {MaterialProperty} property - The property for which the tooltip content is to be built.
	 * @returns {string} The tooltip content string.
	 */
	function buildTooltipContentForPropertyElementWithListElements(
		property: MaterialProperty
	): string {
		const storedProperty = mmpvState.getMaterialProperty(property.id)
		if (!storedProperty || storedProperty.propertyListElements.length === 0) {
			return ''
		}
		let res: string
		switch (property.propertyListElementIds.length) {
			case 1:
				res = $t('UI.mmpv.materialPropertyListElement.addedListElement.one.tt', {
					name: storedProperty.propertyListElements[0].name
				})
				break
			case 2:
				res = $t('UI.mmpv.materialPropertyListElement.addedListElement.two.tt', {
					name: storedProperty.propertyListElements[0].name,
					name2: storedProperty.propertyListElements[1].name
				})
				break
			default:
				res = $t('UI.mmpv.materialPropertyListElement.addedListElement.default.tt', {
					name: storedProperty.propertyListElements[0].name,
					name2: storedProperty.propertyListElements[1].name
				})
		}
		return res
	}
</script>

<div class="material-property-element-list-edit-container">
	<div style="grid-area: name">
		<InputField
			label={$t('UI.mmpv.materialProperty.createListElement.ph')}
			bind:value={propertyElement.name}
			format={inputFieldFormats.FULL}
			{fontProps}
			{headerFontProps}
			errorPath={MATERIAL_PROPERTY_LIST_ELEMENT_FORM_ERROR_PATH_PREFIX + 'name'}
		/>
	</div>
	<div style="grid-area: priority">
		<InputField
			label={$t('UI.mmpv.materialProperty.createListElement.priority.label')}
			bind:value={propertyElement.priority}
			format={inputFieldFormats.FULL}
			{fontProps}
			{headerFontProps}
			errorPath={MATERIAL_PROPERTY_LIST_ELEMENT_FORM_ERROR_PATH_PREFIX + 'priority'}
		/>
	</div>
	<span class="modal-subheader">{$t('UI.mmpv.materialPropertyListElement.values.subheader')}</span>
	<div class="property-list-element-values-container">
		<span style="grid-area: info"
			>{$t('UI.mmpv.propertyListElement.values.hint', {
				name: propertyElement.name
			})}</span
		>
		<div style="grid-area: list">
			<div class="add-property-element">
				<div>
					<MaterialPropertySelection
						bind:materialProperty={selectedProperty}
						alreadyMappedProperties={mappedProperties}
						propertyListElement={{ ...propertyElement }}
					/>
				</div>
				<Button variant="primary" color="green" onclick={addPropertyToElement}>
					<span style="color: inherit; white-space: nowrap"
						>{$t('UI.mmpv.property.addToMaterial')}</span
					>
				</Button>
			</div>
			{const childPropertiesSorted = $derived(
				[...mappedProperties].sort(
					(a: MaterialProperty, b: MaterialProperty) =>
						(a.priority ?? Number.MAX_SAFE_INTEGER) - (b.priority ?? Number.MAX_SAFE_INTEGER)
				) as MaterialProperty[]
			)}
			{#each childPropertiesSorted as property (property.id)}
				<div class="material-property-element">
					<div>
						<span
							role="button"
							tabindex="0"
							onclick={() => onEditMaterialProperty?.(property)}
							onkeydown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') onEditMaterialProperty?.(property)
							}}>{property.name}</span
						>
						{#if property.propertyListElements.length > 0}
							<Tooltip
								maxWidthTtip={370}
								ttipIconHeight="20px"
								ttipIconWidth="20px"
								placementTtip={TooltipPlacements.RIGHT}
								msg={"<div style='line-height: 20px;'> " +
									buildTooltipContentForPropertyElementWithListElements(property) +
									'</div>'}
								isHtml
							/>
						{/if}
					</div>
					<div>
						<button
							class="property-delete-btn"
							title={$t('UI.delete')}
							onclick={() => removePropertyFromListElement(property.id)}
						>
						</button>
					</div>
				</div>
			{/each}
		</div>
	</div>
</div>
<div class="create-new-property-element">
	<Button variant="secondary" color="green" onclick={() => onAddCreatePropertyBreadcrumb?.()}>
		{$t('UI.mmpv.materialProperty.createNew')}
	</Button>
</div>

<style>
	@import '$styles/global.scss';

	.material-property-element-list-edit-container {
		display: grid;
		grid-template-columns: repeat(4, 1fr);
		gap: 1.25rem 1.875rem;
		grid-template-areas:
			'name name name priority'
			'subheader subheader subheader subheader'
			'propertyElements propertyElements propertyElements propertyElements';
		width: 100%;
		align-items: baseline;

		& > span:first-child {
			font-family: Roboto, sans-serif;
			line-height: 15px;
			font-weight: 400;
			font-size: 0.75rem;
			color: var(--primary-green);
		}

		& > span:nth-of-type(2) {
			font-family: Roboto, sans-serif;
			line-height: 15px;
			font-weight: 400;
			font-size: 0.75rem;
			color: black;
		}
	}

	span.modal-subheader {
		font-family: Roboto, sans-serif;
		line-height: 21px;
		font-weight: 500;
		font-size: 0.875rem;
		color: var(--color-blue-500);

		grid-area: subheader;
	}

	.property-list-element-values-container {
		display: grid;
		grid-template-areas:
			'info info info info'
			'list list list list';
		grid-template-columns: 1fr 1fr 1fr 1fr;
		width: 100%;
		grid-area: propertyElements;

		& > span {
			font-family: Roboto, sans-serif;
			line-height: 15px;
			font-weight: 400;
			font-size: 0.75rem;
			color: black;
		}
	}

	.create-new-property-element {
		display: flex;
		flex-direction: row;
		justify-content: flex-start;
		align-items: center;

		width: 100%;
	}

	div.material-property-element {
		display: flex;
		flex-direction: row;
		justify-content: flex-start;
		align-items: flex-end;
		gap: 14px;

		width: 100%;
		border-bottom: 1px solid var(--color-gray-100);
		padding: 0.75rem 0;

		& > div {
			width: 100%;

			&:first-child {
				display: flex;
				flex-direction: row;
				justify-content: flex-start;
				align-items: center;
				gap: 4px;
				font-family: Roboto, sans-serif;
				line-height: 10px;
				font-weight: 400;
				font-size: 0.75rem;
				color: var(--primary-green);
			}

			&:last-child {
				display: flex;
				flex-direction: row;
				align-items: center;
				justify-content: flex-end;
				gap: 8px;

				margin-right: 14px;
			}

			& > span {
				cursor: pointer;
			}
		}

		& .property-delete-btn {
			background: url('@/icons/icon_close.svg') no-repeat center;
			border: none;
			width: 24px;
			height: 24px;
			visibility: hidden;
			cursor: pointer;
		}

		&:hover {
			& .property-delete-btn {
				visibility: visible;
			}
		}
	}

	div.add-property-element {
		display: flex;
		flex-direction: row;
		align-items: flex-end;
		justify-content: flex-start;
		gap: 14px;

		width: 100%;
		padding-top: 20px;

		& > div:first-child {
			min-width: 50%;
			flex-basis: 100%;
		}
	}

	.material-property-element-list-edit-container :global(.sv-control) {
		height: 32px; /* override sv control to have the same height as the usual input fields */
	}

	.material-property-element-list-edit-container :global(.checkbox-container) {
		justify-content: flex-start !important; /* override the checkbox to be at the beginning of the row */
	}
</style>
